import { useLayoutEffect, useRef } from "react";

export const CALENDAR_PX_PER_MS = 34 / 60_000;
export const CALENDAR_EDGE = 36;

export function useCalendarScroll({
  from,
  now,
  follow,
  focusNow,
  onBrowse,
}: {
  from: number;
  now: number;
  follow: boolean;
  focusNow: number;
  onBrowse: () => void;
}) {
  const scroll = useRef<HTMLDivElement>(null);
  const previous = useRef<{
    from: number;
    now: number;
    focusNow: number;
  } | null>(null);
  const expected = useRef(0);

  useLayoutEffect(() => {
    const element = scroll.current;
    if (!element) return;
    const last = previous.current;
    if (last && !follow && from === last.from && focusNow === last.focusNow) {
      // Do not interrupt native touch momentum while the clock keeps ticking.
      previous.current = { from, now, focusNow };
      return;
    }
    let left = element.scrollLeft;
    if (!last || focusNow !== last.focusNow) {
      // A little history remains visible to the right of the sticky labels.
      left = follow
        ? CALENDAR_EDGE + (now - from) * CALENDAR_PX_PER_MS - 70
        : 0;
    } else if (from !== last.from) {
      // Moving the origin must not move the time already under the viewport.
      left = follow ? left + (last.from - from) * CALENDAR_PX_PER_MS : 0;
    } else if (follow) {
      left += (now - last.now) * CALENDAR_PX_PER_MS;
    }
    element.scrollLeft = Math.max(0, left);
    // Read the browser-clamped value, including at either end of a short range.
    expected.current = element.scrollLeft;
    previous.current = { from, now, focusNow };
  }, [from, now, follow, focusNow]);

  function onScroll() {
    const element = scroll.current;
    if (!element) return;
    if (Math.abs(element.scrollLeft - expected.current) > 1) {
      expected.current = element.scrollLeft;
      if (follow) onBrowse();
    }
  }

  return { scroll, onScroll };
}
